// social.login.component.ts - Updated to use service

import { BehaviorSubject, Subscription } from 'rxjs';
import { GoogleLoginComponent } from './google.login/google.login.component';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  Output,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { FacebookLoginComponent } from './facebook.login/facebook.login.component';
import { CheckLoginStatusService } from './checkLoginStatus.service';
import { PostMessageService } from './post-message.service';

@Component({
  selector: 'ig-social-login',
  templateUrl: './social.login.component.html',
  styleUrls: ['./social.login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SocialLoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() mode: 'vertical' | 'horizontal' | 'popup' = 'vertical';
  public googleReady$ = this.checkLoginStatusService.googleReady$;
  public profilePicture: string = null;
  public static loginStatus$ = new BehaviorSubject(false);

  // Add flags to control button visibility
  public showGoogleButton: boolean = false;
  public showFacebookButton: boolean = false;
  public showLoginPopup: boolean = false;
  // Fallback flag: show a simple Google button if the SDK/custom button
  // failed to render (primarily for native app WebView testing)
  public showFallbackGoogleButton: boolean = false;
  public loginError: string = '';

  // Add static properties for app-level login data
  public static appLoginData: any = null;
  public static isAppLevelLogin = false;

  @ViewChild('google', { static: false })
  public googleLoginComponent: GoogleLoginComponent;
  @ViewChild('facebook', { static: false })
  public facebookLoginComponent: FacebookLoginComponent;

  @Output() onLoginStatusChanged: EventEmitter<boolean> = new EventEmitter();
  @Output() onEmailIconClick: EventEmitter<string> = new EventEmitter();

  private viewReady = false;
  private messageListener: (event: MessageEvent) => void;
  private keyDownListener: (event: KeyboardEvent) => void;
  // Prevent postMessage echo loops when syncing login state from host app.
  private isSyncingFromAppMessage = false;
  private lastSentLoginSuccessKey: string | null = null;
  private loginStatusSubscription: Subscription;
  static instance: SocialLoginComponent;

  constructor(
    public checkLoginStatusService: CheckLoginStatusService,
    private changeDetector: ChangeDetectorRef,
    private postMessageService: PostMessageService,
    private ngZone: NgZone
  ) {
    SocialLoginComponent.instance = this;
    const statusOfLogin = this.checkLoginStatusService.isUserLoggedIn;
    SocialLoginComponent.loginStatus$.next(statusOfLogin);

    this.loginStatusSubscription = SocialLoginComponent.loginStatus$.subscribe(
      (status) => {
        this.loginStatusChanged(status);
      }
    );
  }

  ngOnInit() {
    // Check for existing login status
    this.checkInitialLoginStatus();

    // Listen for messages from the parent app.
    // Register OUTSIDE Angular's zone so that the hundreds of third-party
    // postMessage events per second
    // do NOT trigger Angular change detection on every fire.
    // handleAppMessage re-enters the zone only for real app messages.
    this.messageListener = (event: MessageEvent) => {
      this.handleAppMessage(event);
    };
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('message', this.messageListener);
    });

    // Register keydown outside zone — fires on every keypress app-wide.
    // Re-enters zone only when ESC actually closes the popup.
    this.keyDownListener = (event: KeyboardEvent) => {
      this.handleKeyDown(event);
    };
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('keydown', this.keyDownListener);
    });

    // Load Google library and initialize Google login button properly
    this.checkLoginStatusService.loadGoogleLibrary().then(() => {
      // Initialize Google login button after library loads
      this.initializeGoogleButton();
    }).catch((err) => {
      console.warn('Failed to load Google library:', err);
    });
  }

  // Handle keyboard events
  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.showLoginPopup) {
      this.closeLoginPopup();
    }
  }

  // New method to show Google login button
  public async showGoogleLogin() {
    this.showGoogleButton = true;
    this.showFacebookButton = false;

    this.changeDetector.detectChanges();

    // Wait for DOM to update before initializing
    setTimeout(async () => {
      if (this.isInWebView() && this.postMessageService.hasNativeBridge()) {
        await this.checkLoginStatusService.initializeGoogle();
        if (this.googleLoginComponent) {
          setTimeout(() => {
            if (this.googleLoginComponent && typeof (this.googleLoginComponent as any).renderCustomGoogleButton === 'function') {
              (this.googleLoginComponent as any).renderCustomGoogleButton();
            }
          }, 300);
        }
      } else if (this.googleLoginComponent) {
        await this.googleLoginComponent.initializeGoogle();
      } else {
        await this.checkLoginStatusService.initializeGoogle();
      }
    }, 0);
  }

  // New method to show Facebook login button
  public showFacebookLogin() {
    this.showFacebookButton = true;
    this.showGoogleButton = false;

    // Initialize Facebook only when button is clicked
    setTimeout(() => {
      this.initFacebookLogin();
    }, 100); // Small delay to ensure DOM is updated
  }

  // Method to reset login options and return to selection screen
  public resetLoginOptions() {
    this.showGoogleButton = false;
    this.showFacebookButton = false;
    this.changeDetector.detectChanges();
  }

  // Method to open login popup
  public openLoginPopup() {
    this.showLoginPopup = true;
    // Reset button states when opening popup
    this.showGoogleButton = false;
    this.showFacebookButton = false;
    this.showFallbackGoogleButton = false;
    this.loginError = '';
    
    // Force change detection to ensure popup DOM is rendered
    this.changeDetector.detectChanges();
    
    // Ensure Google library is loaded and buttons are initialized
    this.checkLoginStatusService.loadGoogleLibrary().then(() => {
      // Initialize Google button - in WebView this will render custom button, in browser it will render SDK button
      this.initializeGoogleButton();
      this.changeDetector.detectChanges();
      
      // Only render the custom postMessage button when a native bridge exists.
      // Without a bridge the SDK should render its own button (fallback path).
      if (this.isInWebView() && this.postMessageService.hasNativeBridge()) {
        this.renderGoogleButtonInWebView();
      }
      // In a native WebView, verify that *some* button rendered.
      // If neither SDK nor custom button appeared, show the simple fallback.
      if (this.isNativeAppWebView()) {
        setTimeout(() => this.ensureGoogleButtonVisible(), 400);
      }
    }).catch((err) => {
      console.warn('Failed to load Google library:', err);
      this.changeDetector.detectChanges();
    });
  }

  // Helper method to render Google button in WebView with retry logic
  private renderGoogleButtonInWebView(retryCount = 0, maxRetries = 10) {
    // Wait for ViewChild to be available and popup to be fully rendered
    if (this.googleLoginComponent && typeof (this.googleLoginComponent as any).renderCustomGoogleButton === 'function') {
      // Check if popup is actually open in DOM
      const popupOverlay = document.querySelector('.login-popup-overlay');
      if (popupOverlay) {
        // Small delay to ensure DOM is fully updated
        setTimeout(() => {
          (this.googleLoginComponent as any).renderCustomGoogleButton();
        }, 100);
      } else if (retryCount < maxRetries) {
        // Popup not ready yet, retry after a short delay
        setTimeout(() => {
          this.renderGoogleButtonInWebView(retryCount + 1, maxRetries);
        }, 100);
      }
    } else if (retryCount < maxRetries) {
      // ViewChild not available yet, retry after a short delay
      setTimeout(() => {
        this.renderGoogleButtonInWebView(retryCount + 1, maxRetries);
      }, 100);
    }
  }

  // Method to close login popup
  public closeLoginPopup() {
    this.showLoginPopup = false;
    this.showGoogleButton = false;
    this.showFacebookButton = false;
    this.loginError = '';
    this.changeDetector.detectChanges();
  }

  // Method to cancel login process entirely
  public cancelLogin() {
    this.resetLoginOptions();
    // Close any open menus or modals if needed
    // This could be extended based on the specific UI implementation
  }

  // Method to manually trigger Google login (only when user clicks)
  public async initGoogleLogin() {
    await this.login('google');
  }

  // Public method to check Google button status and reinitialize if needed
  public checkGoogleButtonStatus() {
    // Force re-initialization if needed
    this.initializeGoogleButton();
  }

  // Force show Google button for testing
  public forceShowGoogleButton() {
    this.showGoogleButton = true;
    this.showFacebookButton = false;
    this.changeDetector.detectChanges();
  }

  // Initialize Google login button without triggering login
  private async initializeGoogleButton() {
    try {
      if (this.isInWebView()) {
        await this.checkLoginStatusService.initializeGoogle();
      } else if (this.googleLoginComponent) {
        await this.googleLoginComponent.initializeGoogle();
      } else {
        // Retry after a short delay if component isn't ready
        setTimeout(() => this.initializeGoogleButton(), 500);
      }
    } catch (error) {
      console.error('Error initializing Google button:', error);
    }
  }

  // Method to trigger Facebook login
  public async initFacebookLogin() {
    await this.login('facebook');
  }

  // Check for existing login data on component initialization
  private checkInitialLoginStatus() {
    if (SocialLoginComponent.isAppLevelLogin && SocialLoginComponent.appLoginData) {
      this.profilePicture = SocialLoginComponent.appLoginData.profilePicture;
      return;
    }

    FacebookLoginComponent.restoreFromStorage(this.isNativeAppWebView());

    if (SocialLoginComponent.isAppLevelLogin && SocialLoginComponent.appLoginData) {
      this.profilePicture = SocialLoginComponent.appLoginData.profilePicture;
    } else if (this.checkLoginStatusService.isUserLoggedIn) {
      this.profilePicture = this.checkLoginStatusService.profilePicture || '';
    }
  }

  ngAfterViewInit() {
    this.viewReady = true;
    if (SocialLoginComponent.loginStatus$.getValue()) {
      this.changeDetector.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.loginStatusSubscription) {
      this.loginStatusSubscription.unsubscribe();
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
    if (this.keyDownListener) {
      document.removeEventListener('keydown', this.keyDownListener);
    }
  }

  // Handle messages from the parent app.
  // This runs outside Angular's zone (see ngOnInit). Re-enter the zone only
  // when processing real app messages so that Yandex Metrica / other
  // third-party postMessage floods don't trigger change detection.
  private handleAppMessage(event: MessageEvent) {
    // Discard known third-party postMessage noise immediately — before any
    // parsing, logging, or zone re-entry.  Yandex Metrica fires hundreds of
    // __ym__promise_* messages per second; letting them reach Angular's zone
    // caused continuous change detection that blocked keyboard input entirely.
    const raw = event.data;
    if (typeof raw === 'string') {
      if (raw.startsWith('__ym__') || raw.startsWith('yandex')) return;
    } else if (raw && typeof raw === 'object') {
      const t = raw.type;
      if (typeof t === 'string' && (t.startsWith('__ym__') || t.startsWith('yandex'))) return;
    }

    try {
      let message;

      if (typeof raw === 'string') {
        // Check if it's URL-encoded data (contains = and & characters)
        if (raw.includes('=') && raw.includes('&')) {
          // Parse URL-encoded string
          message = this.parseUrlEncodedData(raw);
        } else {
          // Try to parse as JSON
          try {
            message = JSON.parse(raw);
          } catch (jsonError) {
            console.warn('Data is neither JSON nor URL-encoded:', raw);
            return;
          }
        }
      } else {
        // Data is already an object
        message = raw;
      }

      // Re-enter Angular zone only for messages that actually update component state
      this.ngZone.run(() => {
        // Handle different message types
        if (message.type === 'APP_LOGIN_STATUS_UPDATE') {
          this.updateLoginStatus(message.data, true);
        } else if (message.type === 'APP_LOGIN_SUCCESS') {
          // Handle login success response from app
          this.updateLoginStatus(message.data, true);
        } else if (message.type === 'APP_LOGOUT_SUCCESS') {
          // Handle logout success response from app
          this.updateLoginStatus({ isLoggedIn: false }, true);
        } else if (message.type === 'APP_ERROR') {
          // Handle errors from app
          console.error('[PostMessage] Error from app:', message.data);
          if (this.postMessageService) {
            this.postMessageService.sendError({
              type: 'RECEIVED_ERROR',
              message: message.data?.message || 'Unknown error from app',
            });
          }
        } else if (message.type === 'APP_LOGIN_REQUEST' && message.source === 'web-widget') {
          // Our own login request looped back — no native bridge handled it.
          // Show error since SDK fallback buttons are already rendered separately.
          this.handleLoginRequestLoopback(message.data?.provider || message.provider);
        }

        // Handle Facebook login response (legacy format)
        if (message.signed_request || message.long_lived_token) {
          this.handleFacebookLoginResponse(message);
        }
      });
    } catch (error) {
      console.error('Error handling app message:', error);
    }
  }

  private parseUrlEncodedData(data: string): any {
    const params = new URLSearchParams(data);
    const result: any = {};

    for (const [key, value] of params.entries()) {
      // Try to decode URI components
      try {
        result[key] = decodeURIComponent(value);
      } catch (decodeError) {
        result[key] = value;
      }
    }

    return result;
  }

  private handleFacebookLoginResponse(data: any) {
    // Handle Facebook login response
    // Note: Facebook sends access_token (not long_lived_token) in URL-encoded format
    const accessToken = data.access_token || data.long_lived_token;
    const userId = this.extractUserIdFromSignedRequest(data.signed_request);

    // Process the login data as needed
    if (data.signed_request) {
      const userData = this.parseSignedRequest(data.signed_request);
      const resolvedUserId = userData.user_id || userId;
      if (!resolvedUserId) {
        return;
      }

      this.updateLoginStatus({
        isLoggedIn: true,
        userId: resolvedUserId,
        accessToken: accessToken,
        profilePicture: FacebookLoginComponent.resolveProfilePicture(resolvedUserId),
        provider: 'facebook',
      });
    }
  }

  private parseSignedRequest(signedRequest: string): any {
    try {
      const [signature, payload] = signedRequest.split('.');

      // Decode the payload (it's base64url encoded)
      const decodedPayload = this.base64UrlDecode(payload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Error parsing signed request:', error);
      return {};
    }
  }

  private base64UrlDecode(str: string): string {
    // Add padding if needed
    str += '='.repeat((4 - (str.length % 4)) % 4);

    // Replace URL-safe characters
    str = str.replace(/-/g, '+').replace(/_/g, '/');

    // Decode base64
    return atob(str);
  }

  private extractUserIdFromSignedRequest(signedRequest: string): string | null {
    try {
      const userData = this.parseSignedRequest(signedRequest);
      return userData.user_id || null;
    } catch (error) {
      console.error('Error extracting user ID:', error);
      return null;
    }
  }

  // Update login status from app-level OAuth
  private updateLoginStatus(loginData: any, fromAppMessage = false) {
    if (fromAppMessage) {
      this.isSyncingFromAppMessage = true;
    }

    try {
    SocialLoginComponent.appLoginData = loginData;
    SocialLoginComponent.isAppLevelLogin = loginData.isLoggedIn;

    // Use service to update login data
    this.checkLoginStatusService.updateFromAppLogin(loginData);
    
    // Also update component static properties so sendUserInfoAfterLogin can find them
    if (loginData.provider === 'google') {
      GoogleLoginComponent.updateFromAppLogin(loginData);
    } else if (loginData.provider === 'facebook') {
      FacebookLoginComponent.updateFromAppLogin(loginData);
    }

    if (loginData.isLoggedIn) {
      this.profilePicture = loginData.profilePicture;
      if (!SocialLoginComponent.loginStatus$.getValue()) {
        SocialLoginComponent.loginStatus$.next(true);
      }

      // For in-widget login flow (not host-app push), notify host app after state update.
      if (!fromAppMessage && this.postMessageService.isInWebView() && loginData.provider) {
        setTimeout(() => {
          this.sendUserInfoAfterLogin();
        }, 100); // Small delay to ensure state is updated
      }
    } else {
      // Clear both providers when logging out
      this.lastSentLoginSuccessKey = null;
      FacebookLoginComponent.clearLoginData();
      if (this.checkLoginStatusService.provider === 'google') {
        this.checkLoginStatusService.logoutGoogle();
      }
      this.profilePicture = null;
      if (SocialLoginComponent.loginStatus$.getValue()) {
        SocialLoginComponent.loginStatus$.next(false);
      }

      // For in-widget logout flow (not host-app push), notify host app.
      if (!fromAppMessage && this.postMessageService.isInWebView()) {
        this.postMessageService.sendLogoutSuccess();
      }
    }
    } finally {
      this.isSyncingFromAppMessage = false;
    }
  }

  // Send login request to parent app (delegated to PostMessageService)
  public requestAppLogin(provider: 'google' | 'facebook') {
    this.postMessageService.sendLoginRequest(provider);
  }

  // Send logout request to parent app (delegated to PostMessageService)
  public requestAppLogout() {
    const currentProvider =
      this.checkLoginStatusService.provider ||
      SocialLoginComponent.appLoginData?.provider;
    if (currentProvider) {
      this.postMessageService.sendLogoutRequest(currentProvider);
    }
  }

  // Reset button visibility on logout
  public logout() {
    this.showGoogleButton = false;
    this.showFacebookButton = false;
    this.showLoginPopup = false;

    const currentProvider =
      this.checkLoginStatusService.provider ||
      SocialLoginComponent.appLoginData?.provider;

    // Send logout request via postMessage (only if in WebView)
    if (currentProvider && this.postMessageService.isInWebView()) {
      this.postMessageService.sendLogoutRequest(currentProvider);
    }

    // Handle regular logout (components will send logout success if needed)
    if (this.checkLoginStatusService.isUserLoggedIn) {
      if (this.checkLoginStatusService.provider === 'google') {
        this.checkLoginStatusService.logoutGoogle();
        if (this.googleLoginComponent) {
          this.googleLoginComponent.logout();
        } else {
          GoogleLoginComponent.clearLoginData();
        }
      } else if (this.checkLoginStatusService.provider === 'facebook') {
        FacebookLoginComponent.clearLoginData();
        this.checkLoginStatusService.logoutFacebook();
        if (this.facebookLoginComponent) {
          this.facebookLoginComponent.logout();
        } else {
          // Buttons unmounted while logged in — still clear SDK session + statics
          FacebookLoginComponent.clearLoginData();
          try {
            if (window['FB']?.logout) {
              window['FB'].logout(() => {});
            }
          } catch {
            /* ignore */
          }
        }
      }
    } else {
      GoogleLoginComponent.clearLoginData();
      FacebookLoginComponent.clearLoginData();
      if (SocialLoginComponent.loginStatus$.getValue()) {
        SocialLoginComponent.loginStatus$.next(false);
      }
    }
    this.profilePicture = null;
    SocialLoginComponent.appLoginData = null;
    SocialLoginComponent.isAppLevelLogin = false;
    this.changeDetector.detectChanges();

    // Re-render Google only on this instance after *ngIf mounts the button stack.
    setTimeout(() => {
      if (this.googleLoginComponent) {
        this.googleLoginComponent.initializeGoogle();
      } else if (this.mode !== 'popup') {
        this.checkLoginStatusService.renderGoogleButtons();
      }
    }, 50);
  }

  public loginStatusChanged(status: boolean) {
    this.loginError = '';
    if (!status) {
      this.profilePicture = null;
    } else {
      // Set profile picture based on login source
      if (
        SocialLoginComponent.isAppLevelLogin &&
        SocialLoginComponent.appLoginData
      ) {
        this.profilePicture = SocialLoginComponent.appLoginData.profilePicture;
      } else {
        this.profilePicture =
          this.checkLoginStatusService.profilePicture ||
          FacebookLoginComponent.profilePicture ||
          GoogleLoginComponent.profilePicture ||
          '';
      }
      
      // Skip sending when the status comes from host-app message sync.
      if (!this.isSyncingFromAppMessage) {
        this.sendUserInfoAfterLogin();
      }
      
      // Close popup when login succeeds - defer to avoid ASSERTION ERROR
      setTimeout(() => {
        this.closeLoginPopup();
      }, 0);
    }
    this.onLoginStatusChanged.emit(status);
    // Force change detection — Zone.js may not be properly patched in the
    // client-website embedded context, so auto-tick after ngZone.run() is unreliable.
    if (this.viewReady) {
      this.changeDetector.detectChanges();
    }
  }

  // Send user info after successful login
  private sendUserInfoAfterLogin() {
    if (!this.postMessageService.isInWebView()) {
      return; // Only send in WebView context
    }

    const provider = this.checkLoginStatusService.provider || 
                     SocialLoginComponent.appLoginData?.provider;
    
    if (!provider) {
      return;
    }

    if (provider === 'google') {
      const userId = SocialLoginComponent.userId || 
                     GoogleLoginComponent.googleUserId;
      const email = SocialLoginComponent.email || 
                    GoogleLoginComponent.email;
      const userName = SocialLoginComponent.userName || 
                       GoogleLoginComponent.userName;
      const profilePicture = SocialLoginComponent.profilePicture || 
                             GoogleLoginComponent.profilePicture;

      if (userId) {
        const key = `google:${userId}`;
        if (this.lastSentLoginSuccessKey === key) {
          return;
        }
        this.lastSentLoginSuccessKey = key;
        this.postMessageService.sendLoginSuccess({
          provider: 'google',
          userId,
          email,
          userName,
          profilePicture,
        });
      }
    } else if (provider === 'facebook') {
      const userId = SocialLoginComponent.userId || 
                     FacebookLoginComponent.userFacebookId;
      const profilePicture = SocialLoginComponent.profilePicture || 
                             FacebookLoginComponent.profilePicture;
      const accessToken = FacebookLoginComponent.userAccessToken;

      if (userId) {
        const key = `facebook:${userId}`;
        if (this.lastSentLoginSuccessKey === key) {
          return;
        }
        this.lastSentLoginSuccessKey = key;
        this.postMessageService.sendLoginSuccess({
          provider: 'facebook',
          userId,
          profilePicture,
          accessToken,
        });
      }
    }
  }

  public isInWebView(): boolean {
    // Check if we're running inside a webview
    return (
      window.parent !== window ||
      navigator.userAgent.includes('wv') ||
      (navigator.userAgent.includes('Version/') &&
        navigator.userAgent.includes('Mobile/'))
    ); // iOS WebView
  }

  // Environment-specific login methods
  public async loginInWebView(provider: 'google' | 'facebook') {
    if (provider === 'google') {
      try {
        await this.checkLoginStatusService.initializeGoogle();
      } catch (error) {
        console.error('WebView Google login failed:', error);
        // Fallback: show message that Google login may not work in WebView
        this.showWebViewLoginMessage(provider);
      }
    } else if (provider === 'facebook') {
      // Use app-level login for WebView Facebook
      this.requestAppLogin('facebook');
    }
  }

  public async loginInBrowser(provider: 'google' | 'facebook') {
    if (provider === 'google') {
      try {
        if (this.googleLoginComponent) {
          await this.googleLoginComponent.initializeGoogle();
        } else {
          await this.checkLoginStatusService.initializeGoogle();
        }
      } catch (error) {
        console.error('Browser Google login failed:', error);
      }
    } else if (provider === 'facebook') {
      if (this.facebookLoginComponent) {
        this.facebookLoginComponent.login();
      }
    }
  }

  // Unified login method that chooses the right flow
  public async login(provider: 'google' | 'facebook') {
    if (this.isInWebView()) {
      await this.loginInWebView(provider);
    } else {
      await this.loginInBrowser(provider);
    }
  }

  private ensureGoogleButtonVisible() {
    try {
      if (!this.isNativeAppWebView()) {
        return;
      }

      const containers = Array.from(
        document.querySelectorAll('.google-signin-btn')
      ) as HTMLElement[];

      const hasRenderedButton = containers.some((c) =>
        c.querySelector('.abcRioButton, .custom-google-btn')
      );

      if (!hasRenderedButton) {
        this.showFallbackGoogleButton = true;
        this.changeDetector.detectChanges();
      }
    } catch (e) {
      // Fail silently; this is best-effort debug/fallback only
      console.warn('ensureGoogleButtonVisible error', e);
    }
  }

  // Click handler for the simple fallback Google button in WebView
  public onFallbackGoogleLogin() {
    this.loginError = '';

    if (this.postMessageService.hasNativeBridge()) {
      this.requestAppLogin('google');
      return;
    }

    // Try Google SDK prompt as last resort
    const google = (window as any).google;
    if (google?.accounts?.id) {
      try {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            this.loginError = 'Google sign-in is not available in this app. Please try using a browser.';
            this.changeDetector.detectChanges();
          }
        });
        return;
      } catch {
        // fall through to error
      }
    }

    this.loginError = 'Google sign-in is not available in this app. Please try using a browser.';
    this.changeDetector.detectChanges();
  }

  /**
   * Called when an APP_LOGIN_REQUEST we sent loops back to us,
   * meaning no native bridge intercepted it.
   */
  private handleLoginRequestLoopback(provider: string) {
    if (!this.isNativeAppWebView()) return;

    this.loginError = 'Sign-in is not available in this app. Please try using a browser.';
    this.changeDetector.detectChanges();
  }

  private isNativeAppWebView(): boolean {
    const ua = navigator.userAgent || '';

    // --- Android webview detection ---
    const isAndroid = /Android/i.test(ua);
    const hasWvToken = /\bwv\)/i.test(ua);
    const isAndroidWebView =
      isAndroid && (hasWvToken || (/Version\/\d+/i.test(ua) && !/Chrome\/\d+/i.test(ua)));

    // --- iOS webview detection ---
    const isIOS = /iP(ad|hone|od)/i.test(ua);
    const isSafari =
      isIOS &&
      /Safari/i.test(ua) &&
      !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    const isIOSWebView = isIOS && !isSafari;

    return isAndroidWebView || isIOSWebView;
  }

  // Show message for WebView login limitations
  private showWebViewLoginMessage(provider: 'google' | 'facebook') {
    if (provider === 'google') {
      console.warn('Google login may not work properly in WebView environments due to security restrictions');
      // You could show a user-friendly message here
    }
  }

  // Static getters remain the same but could be refactored to use the service
  public static get isUserLoggedIn() {
    if (SocialLoginComponent.isAppLevelLogin) {
      return true;
    }
    // Prefer the shared service — GIS callback may update the service without
    // GoogleLoginComponent statics when CheckLoginStatusService owns initialize().
    if (SocialLoginComponent.instance?.checkLoginStatusService?.isUserLoggedIn) {
      return true;
    }
    return GoogleLoginComponent.isUserLoggedIn || FacebookLoginComponent.isUserLoggedIn;
  }

  public static get userId() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.userId;
    }
    // Get from service instead
    const service = SocialLoginComponent.instance?.checkLoginStatusService;
    if (service?.isUserLoggedIn) {
      if (service.provider === 'google') {
        return localStorage.getItem('social.login.googleUser')
          ? JSON.parse(localStorage.getItem('social.login.googleUser')!).userId
          : '';
      } else if (service.provider === 'facebook') {
        return localStorage.getItem('social.login.facebookUser')
          ? JSON.parse(localStorage.getItem('social.login.facebookUser')!)
              .userId
          : '';
      }
    }
    return '';
  }

  public static get profilePicture() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.profilePicture;
    }
    const service = SocialLoginComponent.instance?.checkLoginStatusService;
    if (service?.isUserLoggedIn) {
      return service.profilePicture || '';
    }
    return '';
  }

  public static get socialName() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.provider;
    }
    const service = SocialLoginComponent.instance?.checkLoginStatusService;
    return service?.provider || '';
  }

  public static get userName() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.userName;
    }
    const service = SocialLoginComponent.instance?.checkLoginStatusService;
    if (service?.isUserLoggedIn && service.provider === 'google') {
      const stored = localStorage.getItem('social.login.googleUser');
      return stored ? JSON.parse(stored).userName : '';
    }
    return '';
  }

  public static get email() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.email;
    }
    const service = SocialLoginComponent.instance?.checkLoginStatusService;
    if (service?.isUserLoggedIn && service.provider === 'google') {
      const stored = localStorage.getItem('social.login.googleUser');
      return stored ? JSON.parse(stored).email : '';
    }
    return '';
  }

  // Instance method to get user display name for the template
  getUserDisplayName(): string {
    if (SocialLoginComponent.isUserLoggedIn) {
      const userName = SocialLoginComponent.userName;
      if (userName && userName.trim()) {
        return userName;
      }
      
      // Fallback to email username
      const email = SocialLoginComponent.email;
      if (email && email.trim()) {
        return email.split('@')[0];
      }
    }
    return 'Guest';
  }

  // Instance method to get email for the template
  getEmail(): string {
    return SocialLoginComponent.email;
  }

  // Check if user is currently in a login flow
  public isInLoginFlow(): boolean {
    return this.showGoogleButton || this.showFacebookButton;
  }

  // Get current login provider being shown
  public getCurrentLoginProvider(): 'google' | 'facebook' | null {
    if (this.showGoogleButton) return 'google';
    if (this.showFacebookButton) return 'facebook';
    return null;
  }
}
